import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { aaiApi } from '../services/aaiApi';
import type { AaiHome, ConsultantHomeData } from '../types/aai';

/*
 * The assigned client a Solution Partner is currently working in.
 *
 * A Solution Partner spans several client companies and every screen in their
 * console reads one of them. The list comes from the portfolio the server
 * already scopes to their assignments, so the switcher can never offer a
 * company they are not assigned to, and the selection is what every scoped
 * read on the pages below is keyed to.
 */

export interface AssignedClient {
  id: string;
  name: string;
  initials: string;
  /** "12 enrolled · 8 onboarded" — the portfolio's own summary line. */
  meta: string;
  stage: string;
  pct: string;
}

interface ActiveClientValue {
  clients: AssignedClient[];
  active: AssignedClient | null;
  setActive: (id: string) => void;
  /** Re-read the portfolio and select `id`. Used after a client is created. */
  adopt: (id: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const STORE_KEY = 'aa_partner_active_client';

const ActiveClientContext = createContext<ActiveClientValue | null>(null);

export const useActiveClient = (): ActiveClientValue => {
  const value = useContext(ActiveClientContext);
  if (!value) throw new Error('useActiveClient is only available inside the Solution Partner shell.');
  return value;
};

const readStored = (): string | null => {
  try {
    return localStorage.getItem(STORE_KEY);
  } catch {
    return null;
  }
};

const writeStored = (id: string) => {
  try {
    localStorage.setItem(STORE_KEY, id);
  } catch {
    /* a browser with storage blocked simply forgets the last client */
  }
};

const isConsultantHome = (home: AaiHome): home is ConsultantHomeData => home.role === 'consultant';

/** The assigned clients when inside the partner shell, none anywhere else. */
export const useAssignedClients = (): AssignedClient[] => {
  const value = useContext(ActiveClientContext);
  if (!value) return [];
  return value.clients;
};

export const ActiveClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [clients, setClients] = useState<AssignedClient[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    aaiApi.getHome()
      .then((res) => {
        if (!live) return;
        if (!isConsultantHome(res.data)) {
          setError('This console is for Solution Partners.');
          return;
        }
        const list = res.data.clients || [];
        setClients(list);
        const stored = readStored();
        const restored = list.find((c) => c.id === stored);
        if (restored) {
          setActiveId(restored.id);
          return;
        }
        if (list.length > 0) setActiveId(list[0].id);
      })
      .catch(() => {
        if (live) setError('Could not load your assigned clients.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
      aaiApi.setAaiCompany(null);
    };
  }, []);

  const setActive = useCallback((id: string) => {
    aaiApi.setAaiCompany(id);
    writeStored(id);
    setActiveId(id);
  }, []);

  // A client that did not exist when the portfolio was read has to be fetched
  // before it can be selected, or the switcher would hold an id it has no row
  // for and every scoped page would read an empty company.
  const adopt = useCallback(async (id: string) => {
    const res = await aaiApi.getHome();
    if (!isConsultantHome(res.data)) return;
    setClients(res.data.clients || []);
    setActive(id);
  }, [setActive]);

  // Applied during render rather than in an effect. A child's fetch effect runs
  // BEFORE its parent's, so scoping here from an effect would let the first read
  // on every page go out unscoped and answer for the wrong company.
  if (activeId) aaiApi.setAaiCompany(activeId);

  /*
   * Memoised, and that is load-bearing rather than an optimisation.
   *
   * `find` returns a new object identity on every render of this provider. A
   * consumer with `[active]` in an effect's dependencies would therefore re-run
   * that effect on every render, and if the effect sets state it re-renders the
   * provider and loops — which is exactly what the shell's nav-count effect did
   * until this memo. Consumers should still key on `active?.id` where they can.
   */
  const active = useMemo(
    () => clients.find((c) => c.id === activeId) || null,
    [clients, activeId],
  );

  const value = useMemo(
    () => ({ clients, active, setActive, adopt, loading, error }),
    [clients, active, setActive, adopt, loading, error],
  );

  return (
    <ActiveClientContext.Provider value={value}>
      {children}
    </ActiveClientContext.Provider>
  );
};
